import React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../utils"

const badgeStyles = cva("rounded-full inline-flex items-center font-semibold", {
  variants: {
    size: {
      sm: "gap-[0.125rem] not-has-data-[icon]:px-[0.375rem] text-xs leading-5 has-data-[icon=inline-start]:pl-[0.125rem] has-data-[icon=inline-start]:pr-1 has-data-[icon=inline-end]:pl-1 has-data-[icon=inline-end]:pr-[0.125rem]",
      lg: "gap-1 not-has-data-[icon]:px-2 text-sm leading-6 has-data-[icon=inline-start]:pl-1 has-data-[icon=inline-start]:pr-[0.375rem] has-data-[icon=inline-end]:pl-[0.375rem] has-data-[icon=inline-end]:pr-1"
    },
    color: {
      black: "",
      blue: "",
      darkBlue: "",
      gray: "",
      green: "",
      orange: "",
      purple: "",
      red: "",
      white: "",
      yellow: ""
    },
    variant: {
      solid: "",
      outline: "bg-transparent ring-1",
      light: ""
    }
  },
  compoundVariants: [
    { color: "black", variant: "solid", class: "bg-gray-900 text-white" },
    {
      color: "black",
      variant: "outline",
      class: "ring-gray-900 text-gray-900"
    },
    { color: "black", variant: "light", class: "bg-gray-900 text-white" },
    { color: "blue", variant: "solid", class: "bg-blue-500 text-white" },
    { color: "blue", variant: "outline", class: "ring-blue-600 text-blue-600" },
    { color: "blue", variant: "light", class: "bg-blue-100 text-blue-800" },
    { color: "darkBlue", variant: "solid", class: "bg-blue-800 text-white" },
    {
      color: "darkBlue",
      variant: "outline",
      class: "ring-blue-800 text-blue-800"
    },
    { color: "darkBlue", variant: "light", class: "bg-blue-800 text-white" },
    { color: "gray", variant: "solid", class: "bg-gray-800 text-white" },
    { color: "gray", variant: "outline", class: "ring-gray-800 text-gray-800" },
    { color: "gray", variant: "light", class: "bg-gray-100 text-gray-900" },
    { color: "green", variant: "solid", class: "bg-green-500 text-white" },
    {
      color: "green",
      variant: "outline",
      class: "ring-green-600 text-green-600"
    },
    { color: "green", variant: "light", class: "bg-green-100 text-green-800" },
    { color: "orange", variant: "solid", class: "bg-orange-500 text-white" },
    {
      color: "orange",
      variant: "outline",
      class: "ring-orange-600 text-orange-600"
    },
    {
      color: "orange",
      variant: "light",
      class: "bg-orange-100 text-orange-800"
    },
    { color: "purple", variant: "solid", class: "bg-purple-500 text-white" },
    {
      color: "purple",
      variant: "outline",
      class: "ring-purple-600 text-purple-600"
    },
    {
      color: "purple",
      variant: "light",
      class: "bg-purple-100 text-purple-800"
    },
    { color: "red", variant: "solid", class: "bg-red-500 text-white" },
    { color: "red", variant: "outline", class: "ring-red-600 text-red-600" },
    { color: "red", variant: "light", class: "bg-red-100 text-red-800" },
    { color: "white", variant: "solid", class: "bg-white text-gray-900" },
    { color: "white", variant: "outline", class: "ring-white text-white" },
    { color: "white", variant: "light", class: "bg-white text-gray-900" },
    {
      color: "yellow",
      variant: "solid",
      class: "bg-system-yellow-500 text-white"
    },
    {
      color: "yellow",
      variant: "outline",
      class: "ring-system-yellow-600 text-system-yellow-600"
    },
    {
      color: "yellow",
      variant: "light",
      class: "bg-system-yellow-100 text-system-yellow-800"
    }
  ],
  defaultVariants: {
    size: "sm",
    color: "gray",
    variant: "solid"
  }
})

type BadgeVariantProps = VariantProps<typeof badgeStyles>

export type BadgeProps = {
  /** Content displayed inside the badge */
  children: React.ReactNode
  /** Color treatment applied to the badge */
  color?: NonNullable<BadgeVariantProps["color"]>
  /** Badge size */
  size?: NonNullable<BadgeVariantProps["size"]>
  /** Visual style of the color treatment */
  variant?: NonNullable<BadgeVariantProps["variant"]>
} & React.ComponentPropsWithoutRef<"span">

/**
 * A small label for categorizing or highlighting status.
 * Supports multiple colors, variants, and sizes.
 */
export function Badge({
  children,
  className,
  color,
  variant,
  size,
  ...props
}: BadgeProps) {
  return (
    <span
      className={cn(badgeStyles({ color, variant, size }), className)}
      data-slot="badge"
      {...props}
    >
      {children}
    </span>
  )
}

export default Badge
